import { LoadingButton } from '@mui/lab';
import { Box, Stack, Typography } from '@mui/material';
import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { DialogComponentProps, useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { formatCurrency } from 'common/utils/formatUtils';
import { useCreateFinancialReserveMutation } from 'generated/graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailRow } from '../DetailRow/DetailRow';
import {
  EditFinancialReserveAmountDialog,
  EditFinancialReserveAmountDialogProps,
} from '../EditFinancialReserveAmountDialog/EditFinancialReserveAmountDialog';
import {
  EditFinancialReserveTextDialog,
  EditFinancialReserveTextDialogProps,
} from '../EditFinancialReserveTextDialog/EditFinancialReserveTextDialog';
import { styles } from './AddFinancialReserveDialog.styles';

const AddFinancialReserveDialogContent: React.FC<DialogComponentProps<void>> = ({ onResolve }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();
  const [amount, setAmount] = useState<number>();
  const [text, setText] = useState<string>();

  const financialReserveAmountDialog = useDialog<EditFinancialReserveAmountDialogProps, number>(
    EditFinancialReserveAmountDialog,
    {
      instanceId: `set-financial-reserve-amount`,
    },
  );

  const handleAmountClick = async () => {
    const newAmount = await financialReserveAmountDialog.create({ amount });
    setAmount(newAmount);
  };

  const financialReserveTextDialog = useDialog<EditFinancialReserveTextDialogProps, string>(
    EditFinancialReserveTextDialog,
    {
      instanceId: `set-financial-reserve-text`,
    },
  );

  const handleTextClick = async () => {
    const newText = await financialReserveTextDialog.create({ text });
    setText(newText);
  };

  const [createFinancialReserve, { loading }] = useCreateFinancialReserveMutation({
    onCompleted: () => {
      onResolve();
    },
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
      onResolve();
    },
    refetchQueries: ['FinancialReserves'],
  });

  const handleSubmit = async () => {
    if (!!amount && !!text) {
      await createFinancialReserve({
        variables: {
          financialReserve: {
            amount: { amount, currency: 'EUR' },
            text,
          },
        },
      });
    } else {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
      onResolve();
    }
  };

  return (
    <Stack>
      <Box textAlign="center">
        <Typography variant="headlineXL">{t('fintelligenceReport.addFinancialReserveDialog.title')}</Typography>
      </Box>

      <Stack sx={styles.container}>
        <DetailRow<number>
          formatter={(value: number) => formatCurrency(value ?? 0, i18n.language)}
          label={t('fintelligenceReport.addFinancialReserveDialog.amount')}
          onClick={handleAmountClick}
          value={amount}
        />
        <DetailRow<string>
          label={t('fintelligenceReport.addFinancialReserveDialog.text')}
          onClick={handleTextClick}
          value={text}
        />
      </Stack>

      <Box mt={8}>
        <LoadingButton
          color="primary"
          disabled={!amount || !text}
          fullWidth
          loading={loading}
          onClick={handleSubmit}
          size="large"
          type="submit"
        >
          <Typography color="white" variant="bodyL">
            {t('fintelligenceReport.addFinancialReserveDialog.button')}
          </Typography>
        </LoadingButton>
      </Box>
    </Stack>
  );
};

export const AddFinancialReserveDialog: React.FC<DialogComponentProps<void>> = (props) => {
  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <AddFinancialReserveDialogContent {...props} />
    </ResponsiveSideDialog>
  );
};
